import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';

interface CartItem {
  id: string;
  name: string;
  description: string;
  price: number;
  quantity: number;
}

interface CartPageProps {
  currentUser?: any;
  onAuthRequired?: () => boolean;
  deliveryInfo?: any;
}

const CartPage = ({ currentUser, onAuthRequired, deliveryInfo }: CartPageProps) => {
  const navigate = useNavigate();
  const [cart, setCart] = useState<CartItem[]>([]);

  useEffect(() => {
    // Load cart from localStorage
    const savedCart = localStorage.getItem('deliveryCart');
    if (savedCart) {
      setCart(JSON.parse(savedCart));
    }
  }, []);

  useEffect(() => {
    // Save cart to localStorage whenever it changes
    localStorage.setItem('deliveryCart', JSON.stringify(cart));
  }, [cart]);

  const updateQuantity = (itemId: string, change: number) => {
    setCart(prevCart => {
      return prevCart.map(item => {
        if (item.id === itemId) {
          const newQuantity = Math.max(0, item.quantity + change);
          return newQuantity === 0 
            ? null 
            : { ...item, quantity: newQuantity };
        }
        return item;
      }).filter(Boolean) as CartItem[];
    });
  };

  const removeItem = (itemId: string) => {
    setCart(prevCart => prevCart.filter(item => item.id !== itemId));
    toast.success('Item removed from cart');
  };

  const getCartTotal = () => {
    return cart.reduce((total, item) => total + (item.price * item.quantity), 0);
  };

  const getCartItemCount = () => {
    return cart.reduce((count, item) => count + item.quantity, 0);
  };

  const deliveryFee = deliveryInfo?.deliveryFee || 30; // Dynamic delivery fee based on location
  const totalAmount = getCartTotal() + deliveryFee;

  const proceedToCheckout = () => {
    if (cart.length === 0) {
      toast.error('Your cart is empty');
      return;
    }
    navigate('/checkout');
  };

  if (cart.length === 0) {
    return (
      <div style={{ minHeight: '100vh', background: '#f9fafb' }}>
        {/* Header */}
        <header style={{ 
          background: 'white', 
          boxShadow: '0 1px 3px rgba(0,0,0,0.1)' 
        }}>
          <div style={{ 
            maxWidth: '448px',
            margin: '0 auto',
            padding: '16px', 
            display: 'flex', 
            alignItems: 'center' 
          }}>
            <button 
              onClick={() => navigate(-1)} 
              style={{ 
                padding: '8px', 
                marginLeft: '-8px',
                border: 'none',
                background: 'none',
                cursor: 'pointer',
                fontSize: '18px'
              }}
            >
              ←
            </button>
            <h1 style={{ 
              fontSize: '20px', 
              fontWeight: '600', 
              color: '#111827', 
              marginLeft: '16px',
              margin: 0
            }}>
              Cart
            </h1>
          </div>
        </header>

        {/* Empty State */}
        <div style={{ 
          maxWidth: '448px',
          margin: '0 auto',
          padding: '64px 16px', 
          textAlign: 'center' 
        }}>
          <div style={{ fontSize: '60px', marginBottom: '16px' }}>🛒</div>
          <h2 style={{ 
            fontSize: '20px', 
            fontWeight: '600', 
            color: '#111827', 
            marginBottom: '8px' 
          }}>
            Your cart is empty
          </h2>
          <p style={{ 
            color: '#6b7280', 
            marginBottom: '32px' 
          }}>
            Add some delicious items to get started
          </p>
          <button
            onClick={() => navigate('/menu')}
            className="btn btn-primary"
            style={{
              background: '#f97316',
              color: 'white',
              padding: '12px 24px',
              borderRadius: '8px',
              fontWeight: '600',
              border: 'none',
              cursor: 'pointer'
            }}
          >
            Browse Menu
          </button>
        </div>
      </div>
    );
  }

  return (
    <div style={{ minHeight: '100vh', background: '#f9fafb', paddingBottom: '120px' }}>
      {/* Header */}
      <header style={{ 
        background: 'white', 
        boxShadow: '0 1px 3px rgba(0,0,0,0.1)', 
        position: 'sticky', 
        top: 0, 
        zIndex: 10 
      }}>
        <div style={{ 
          maxWidth: '448px',
          margin: '0 auto',
          padding: '16px', 
          display: 'flex', 
          alignItems: 'center' 
        }}>
          <button 
            onClick={() => navigate(-1)} 
            style={{ 
              padding: '8px', 
              marginLeft: '-8px',
              border: 'none',
              background: 'none',
              cursor: 'pointer',
              fontSize: '18px'
            }}
          >
            ←
          </button>
          <h1 style={{ 
            fontSize: '20px', 
            fontWeight: '600', 
            color: '#111827', 
            marginLeft: '16px',
            margin: 0
          }}>
            Cart ({getCartItemCount()} items)
          </h1>
        </div>
      </header>

      {/* Cart Items */}
      <div style={{ 
        maxWidth: '448px',
        margin: '0 auto',
        padding: '16px' 
      }}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
          {cart.map(item => (
            <div key={item.id} style={{
              background: 'white',
              borderRadius: '8px',
              boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
              padding: '16px'
            }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                <div style={{ flex: 1, paddingRight: '16px' }}>
                  <h3 style={{ 
                    fontWeight: '600', 
                    color: '#111827', 
                    marginBottom: '4px',
                    margin: '0 0 4px 0'
                  }}>
                    {item.name}
                  </h3>
                  <p style={{ 
                    fontSize: '14px', 
                    color: '#6b7280', 
                    marginBottom: '8px',
                    margin: '0 0 8px 0'
                  }}>
                    {item.description}
                  </p>
                  <p style={{ 
                    fontSize: '18px', 
                    fontWeight: '600', 
                    color: '#f97316',
                    margin: 0
                  }}>
                    ₹{item.price}
                  </p>
                </div>
                <div style={{ 
                  display: 'flex', 
                  flexDirection: 'column', 
                  alignItems: 'flex-end', 
                  gap: '8px' 
                }}>
                  <button
                    onClick={() => removeItem(item.id)}
                    style={{
                      color: '#ef4444',
                      background: 'none',
                      border: 'none',
                      cursor: 'pointer',
                      padding: '4px',
                      fontSize: '16px'
                    }}
                  >
                    🗑️
                  </button>
                  <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    background: '#f3f4f6',
                    borderRadius: '8px'
                  }}>
                    <button
                      onClick={() => updateQuantity(item.id, -1)}
                      style={{
                        padding: '8px',
                        color: '#6b7280',
                        background: 'none',
                        border: 'none',
                        cursor: 'pointer',
                        borderRadius: '8px 0 0 8px'
                      }}
                    >
                      −
                    </button>
                    <span style={{
                      padding: '8px 12px',
                      color: '#111827',
                      fontWeight: '500',
                      minWidth: '48px',
                      textAlign: 'center'
                    }}>
                      {item.quantity}
                    </span>
                    <button
                      onClick={() => updateQuantity(item.id, 1)}
                      style={{
                        padding: '8px',
                        color: '#6b7280',
                        background: 'none',
                        border: 'none',
                        cursor: 'pointer',
                        borderRadius: '0 8px 8px 0'
                      }}
                    >
                      +
                    </button>
                  </div>
                </div>
              </div>
              <div style={{ 
                marginTop: '8px', 
                paddingTop: '8px', 
                borderTop: '1px solid #f3f4f6' 
              }}>
                <p style={{ 
                  textAlign: 'right', 
                  color: '#111827', 
                  fontWeight: '500',
                  margin: 0
                }}>
                  ₹{(item.price * item.quantity).toFixed(2)}
                </p>
              </div>
            </div>
          ))}
        </div>

        {/* Order Summary */}
        <div style={{
          marginTop: '24px',
          background: 'white',
          borderRadius: '8px',
          boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
          padding: '16px'
        }}>
          <h3 style={{ 
            fontWeight: '600', 
            color: '#111827', 
            marginBottom: '16px',
            margin: '0 0 16px 0'
          }}>
            Order Summary
          </h3>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', color: '#6b7280' }}>
              <span>Subtotal</span>
              <span>₹{getCartTotal().toFixed(2)}</span>
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', color: '#6b7280' }}>
              <span>
                Delivery Fee
                {deliveryInfo?.zone && (
                  <span style={{ fontSize: '12px', color: '#9ca3af', marginLeft: '4px' }}>
                    ({deliveryInfo.distance} km - {deliveryInfo.zone.name})
                  </span>
                )}
              </span>
              <span>₹{deliveryFee.toFixed(2)}</span>
            </div>
            <div style={{ 
              borderTop: '1px solid #e5e7eb', 
              paddingTop: '8px', 
              marginTop: '8px' 
            }}>
              <div style={{ 
                display: 'flex', 
                justifyContent: 'space-between', 
                fontSize: '18px', 
                fontWeight: '600', 
                color: '#111827' 
              }}>
                <span>Total</span>
                <span>₹{totalAmount.toFixed(2)}</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Checkout Button */}
      <div style={{
        position: 'fixed',
        bottom: 0,
        left: '50%',
        transform: 'translateX(-50%)',
        width: '100%',
        maxWidth: '448px',
        background: 'white',
        borderTop: '1px solid #e5e7eb',
        padding: '16px'
      }}>
        <button
          onClick={proceedToCheckout}
          className="btn btn-primary"
          style={{
            width: '100%',
            background: '#f97316',
            color: 'white',
            padding: '16px',
            borderRadius: '8px',
            fontWeight: '600',
            fontSize: '18px',
            border: 'none',
            cursor: 'pointer'
          }}
        >
          Proceed to Checkout - ₹{totalAmount.toFixed(2)}
        </button>
      </div>
    </div>
  );
};

export default CartPage;